import React, { useState, useEffect } from 'react';
import { FileText, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useSearchParams } from 'react-router-dom';
import HRAQuestionnaireStreamlined from './HRAQuestionnaireStreamlined';
import HRADashboard from './HRADashboard';

const HRAMain = ({ user }) => {
  const [searchParams] = useSearchParams();
  const [currentView, setCurrentView] = useState('loading'); // loading, status, questionnaire, dashboard
  const [hraStatus, setHraStatus] = useState(null);
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { apiCall } = useApp();
  
  // Get set_number from URL params, if not provided, API will use default set
  const setNumberParam = searchParams.get('set_number');
  const setNumber = setNumberParam ? parseInt(setNumberParam, 10) : undefined;

  useEffect(() => {
    checkHRAStatus();
  }, []);

  const checkHRAStatus = async () => {
    try {
      setIsLoading(true);
      const status = await apiCall('/hra/status', 'GET');
      setHraStatus(status);
      
      if (status.has_completed_hra && !status.hra_expired) {
        // Load existing risk assessment
        const assessment = await apiCall('/hra/risk-assessment', 'GET');
        setRiskAssessment(assessment);
        setCurrentView('dashboard');
      } else {
        setCurrentView('status');
      }
    } catch (error) {
      console.error('Failed to check HRA status:', error);
      setCurrentView('status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartQuestionnaire = () => {
    setCurrentView('questionnaire');
  };

  const handleQuestionnaireComplete = (assessment) => {
    setRiskAssessment(assessment);
    setCurrentView('dashboard');
    // Refresh status
    checkHRAStatus();
  };

  const handleRetakeAssessment = () => {
    setCurrentView('questionnaire');
  };

  const handleCancelQuestionnaire = () => {
    setCurrentView('status');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading health assessment...</p>
        </div>
      </div>
    );
  }

  if (currentView === 'questionnaire') {
    return (
      <HRAQuestionnaireStreamlined
        user={user}
        onComplete={handleQuestionnaireComplete}
        onCancel={handleCancelQuestionnaire}
        setNumber={setNumber}
      />
    );
  }

  if (currentView === 'dashboard') {
    return (
      <HRADashboard
        user={user}
        riskAssessment={riskAssessment}
        onRetakeAssessment={handleRetakeAssessment}
      />
    );
  }

  // Status view - show when no HRA completed or expired
  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <FileText className="w-16 h-16 text-primary-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Health Risk Assessment</h1>
        <p className="text-lg text-gray-600">
          Get personalized insights about your health risks and recommendations
        </p>
      </div>

      {/* HRA Status Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        {hraStatus?.has_completed_hra ? (
          <div className="text-center">
            {hraStatus.hra_expired ? (
              <>
                <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Assessment Expired</h2>
                <p className="text-gray-600 mb-4">
                  Your health risk assessment has expired. We recommend retaking it to get updated insights.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Last completed: {new Date(hraStatus.last_hra_date).toLocaleDateString()}
                </p>
              </>
            ) : (
              <>
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Assessment Complete</h2>
                <p className="text-gray-600 mb-4">
                  You have completed your health risk assessment.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Completed: {new Date(hraStatus.last_hra_date).toLocaleDateString()}
                  {hraStatus.days_until_expiry && (
                    <span className="block mt-1">
                      Expires in {hraStatus.days_until_expiry} days
                    </span>
                  )}
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="text-center">
            <Clock className="w-12 h-12 text-blue-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Ready to Start</h2>
            <p className="text-gray-600 mb-6">
              Complete your first health risk assessment to get personalized health insights and recommendations.
            </p>
          </div>
        )}

        <div className="flex justify-center">
          <button
            onClick={handleStartQuestionnaire}
            className="px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            {hraStatus?.has_completed_hra ? 'Retake Assessment' : 'Start Assessment'}
          </button>
        </div>
      </div>

      {/* What to Expect */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">What to Expect</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <FileText className="w-6 h-6" />
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Comprehensive Questions</h4>
            <p className="text-sm text-gray-600">
              Answer questions about your medical history, lifestyle, and family health
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6" />
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Risk Analysis</h4>
            <p className="text-sm text-gray-600">
              Get personalized risk scores for various health conditions
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <Clock className="w-6 h-6" />
            </div>
            <h4 className="font-medium text-gray-900 mb-2">Recommendations</h4>
            <p className="text-sm text-gray-600">
              Receive actionable recommendations to improve your health
            </p>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Benefits of Health Risk Assessment</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Early Detection</h4>
              <p className="text-sm text-gray-600">Identify potential health risks before they become serious problems</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Personalized Insights</h4>
              <p className="text-sm text-gray-600">Get recommendations tailored to your specific risk factors</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Preventive Care</h4>
              <p className="text-sm text-gray-600">Take proactive steps to maintain and improve your health</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900">Healthcare Planning</h4>
              <p className="text-sm text-gray-600">Make informed decisions about your healthcare needs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Privacy & Accuracy Notice</p>
            <ul className="space-y-1 text-blue-700">
              <li>• Your health information is encrypted and secure</li>
              <li>• This assessment is for informational purposes only</li>
              <li>• Results do not replace professional medical advice</li>
              <li>• Consult your healthcare provider for medical decisions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRAMain;
